import { supabase } from '@/lib/supabase';

/** Columns used by management dashboard / project list (avoid optional JSON columns). */
export const PROJECT_LIST_COLUMNS = [
  'id',
  'project_name',
  'project_code',
  'client_name',
  'site_address',
  'city',
  'state',
  'country',
  'status',
  'summary',
  'start_date',
  'target_completion_date',
  'actual_completion_date',
  'project_type',
  'cover_image',
  'cover_photo_url',
  'client_user_id',
  'project_progress',
  'schedule_progress',
  'checklist_progress',
  'pending_schedule_items',
  'current_stage',
  'created_at',
  'updated_at',
].join(',');

export const PROJECT_SELECT_COLUMNS = [
  'id',
  'project_name',
  'site_address',
  'project_type',
  'status',
  'current_stage',
  'updated_at',
].join(',');

export interface FetchRoleBasedProjectsResult {
  rows: Record<string, unknown>[];
  error: string | null;
  profileId: string | null;
  role: string | null;
}

/** PostgREST select lists must be comma-separated strings — never pass raw arrays. */
export function normalizeSelectColumns(columns: unknown, fallback = PROJECT_LIST_COLUMNS): string {
  if (typeof columns === 'string') {
    const trimmed = columns.trim();
    return trimmed || fallback;
  }
  if (Array.isArray(columns)) {
    const joined = columns
      .filter((col): col is string => typeof col === 'string')
      .map((col) => col.trim())
      .filter(Boolean)
      .join(',');
    return joined || fallback;
  }
  return fallback;
}

export function normalizeUserRole(role: unknown): string | null {
  if (typeof role === 'string') {
    const normalized = role.trim().toLowerCase();
    return normalized || null;
  }
  if (role == null) return null;
  if (typeof role === 'number' || typeof role === 'boolean') {
    const normalized = String(role).trim().toLowerCase();
    return normalized || null;
  }
  return null;
}

export function normalizeProfileId(id: unknown): string | null {
  if (typeof id === 'string') {
    const trimmed = id.trim();
    return trimmed || null;
  }
  if (id == null) return null;
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }
  return null;
}

function logProjectsQueryError(
  table: string,
  role: string | null,
  profileId: string | null,
  err: { message?: string; code?: string; details?: string; hint?: string }
) {
  console.error(`[fetchRoleBasedProjects] ${table} query failed`, {
    table,
    role,
    profileId,
    message: err.message,
    code: err.code,
    details: err.details,
    hint: err.hint,
  });
}

type SubcontractorMemberProjectRow = {
  project_id: string;
  member_role?: string;
  access_scope?: string | null;
  jphph_projects: Record<string, unknown> | Record<string, unknown>[] | null;
};

/**
 * Load all projects assigned to a subcontractor via jphph_project_members.
 * Uses a membership join first, then batch/per-id fallbacks for RLS edge cases.
 * Does not filter by access_scope.
 */
async function fetchProjectsForSubcontractorMemberships(
  profileId: string,
  columns: string
): Promise<{ rows: Record<string, unknown>[]; error: string | null }> {
  const { data: memberRows, error: memberErr } = await supabase
    .from('jphph_project_members')
    .select(`project_id, member_role, access_scope, jphph_projects (${columns})`)
    .eq('user_id', profileId)
    .eq('member_role', 'subcontractor');

  let resolvedMemberRows = memberRows as SubcontractorMemberProjectRow[] | null;

  if (memberErr) {
    console.warn('[fetchRoleBasedProjects] subcontractor join query failed, falling back to members-only', {
      profileId,
      message: memberErr.message,
    });

    const { data: memberOnlyRows, error: memberOnlyErr } = await supabase
      .from('jphph_project_members')
      .select('project_id, member_role, access_scope')
      .eq('user_id', profileId)
      .eq('member_role', 'subcontractor');

    if (memberOnlyErr) {
      logProjectsQueryError('jphph_project_members', 'subcontractor', profileId, memberOnlyErr);
      return {
        rows: [],
        error: `Project members query failed (${memberOnlyErr.code ?? 'unknown'}): ${memberOnlyErr.message}`,
      };
    }

    resolvedMemberRows = (memberOnlyRows ?? []).map((row) => ({
      project_id: row.project_id as string,
      member_role: row.member_role as string | undefined,
      access_scope: row.access_scope as string | null | undefined,
      jphph_projects: null,
    }));
  }

  const assignedProjectIds = Array.from(
    new Set((resolvedMemberRows ?? []).map((r) => r.project_id as string).filter(Boolean))
  );

  if (assignedProjectIds.length === 0) {
    return { rows: [], error: null };
  }

  const rowsById = new Map<string, Record<string, unknown>>();

  for (const member of resolvedMemberRows ?? []) {
    const nested = member.jphph_projects;
    if (!nested) continue;
    const project = (Array.isArray(nested) ? nested[0] : nested) as Record<string, unknown>;
    const id = project.id as string | undefined;
    if (id) rowsById.set(id, project);
  }

  let missingIds = assignedProjectIds.filter((id) => !rowsById.has(id));

  if (missingIds.length > 0) {
    console.warn('[fetchRoleBasedProjects] subcontractor join missed projects, fetching by id', {
      profileId,
      assignedCount: assignedProjectIds.length,
      missingCount: missingIds.length,
    });

    const { data: batchRows, error: batchErr } = await supabase
      .from('jphph_projects')
      .select(columns)
      .in('id', missingIds);

    if (batchErr) {
      logProjectsQueryError('jphph_projects', 'subcontractor', profileId, batchErr);
    } else {
      for (const row of (batchRows ?? []) as Record<string, unknown>[]) {
        const id = row.id as string;
        if (id) rowsById.set(id, row);
      }
    }

    missingIds = assignedProjectIds.filter((id) => !rowsById.has(id));

    for (const id of missingIds) {
      const { data: singleRow, error: singleErr } = await supabase
        .from('jphph_projects')
        .select(columns)
        .eq('id', id)
        .maybeSingle();

      if (singleErr) {
        logProjectsQueryError('jphph_projects', 'subcontractor', profileId, singleErr);
        continue;
      }
      if (singleRow) rowsById.set(id, singleRow as Record<string, unknown>);
    }
  }

  const rows = assignedProjectIds
    .map((id) => rowsById.get(id))
    .filter((row): row is Record<string, unknown> => Boolean(row));

  rows.sort(
    (a, b) =>
      new Date(String(b.updated_at ?? 0)).getTime() - new Date(String(a.updated_at ?? 0)).getTime()
  );

  if (rows.length !== assignedProjectIds.length) {
    console.warn('[fetchRoleBasedProjects] subcontractor project count mismatch', {
      profileId,
      assignedProjectIds,
      loadedProjectIds: rows.map((r) => r.id),
    });
  }

  return { rows, error: null };
}

/**
 * Load projects visible to the current user based on jphph_profiles.user_type.
 * - management: all rows in jphph_projects (no jphph_project_members filter)
 * - subcontractor: jphph_project_members where member_role = subcontractor
 * - client: client_user_id match OR jphph_project_members where member_role = client
 */
export async function fetchRoleBasedProjects(options?: {
  columns?: string | string[];
  limit?: number;
  /** When provided by useAuth, skips redundant getSession/profile round-trips. */
  profileId?: string;
  role?: string;
}): Promise<FetchRoleBasedProjectsResult> {
  try {
  const columns = normalizeSelectColumns(options?.columns);

  let profileId: string | null = null;
  let role: string | null = null;

  if (options?.profileId != null && options?.role != null) {
    profileId = normalizeProfileId(options.profileId);
    role = normalizeUserRole(options.role);
  } else {
    const { data: { session }, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) {
      logProjectsQueryError('auth.session', null, null, sessionErr);
      return {
        rows: [],
        error: `Auth session error: ${sessionErr.message}`,
        profileId: null,
        role: null,
      };
    }

    if (!session?.user) {
      return { rows: [], error: 'Not signed in', profileId: null, role: null };
    }

    const userId = session.user.id;

    const { data: profile, error: profileErr } = await supabase
      .from('jphph_profiles')
      .select('id, email, user_type, is_active')
      .eq('id', userId)
      .maybeSingle();

    if (profileErr) {
      logProjectsQueryError('jphph_profiles', null, userId, profileErr);
      return {
        rows: [],
        error: `Profile load failed (${profileErr.code ?? 'unknown'}): ${profileErr.message}`,
        profileId: userId,
        role: null,
      };
    }

    if (!profile) {
      console.error('[fetchRoleBasedProjects] jphph_profiles: no row for auth user', { profileId: userId });
      return {
        rows: [],
        error: 'Profile not found for current user',
        profileId: userId,
        role: null,
      };
    }

    if (!profile.is_active) {
      return {
        rows: [],
        error: 'Account is inactive',
        profileId: profile.id as string,
        role: (profile.user_type as string) ?? null,
      };
    }

    role = normalizeUserRole(profile.user_type);
    profileId = normalizeProfileId(profile.id);
  }

  if (!profileId || !role) {
    return {
      rows: [],
      error: 'Missing profile or role for project fetch',
      profileId,
      role,
    };
  }

  console.log('[fetchRoleBasedProjects] start', { table: 'jphph_projects', role, profileId });

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    console.warn('[fetchRoleBasedProjects] no auth session — project list may be empty due to RLS', {
      role,
      profileId,
    });
    return {
      rows: [],
      error: 'Not signed in — cannot load projects',
      profileId,
      role,
    };
  }

  if (role === 'management') {
    let query = supabase
      .from('jphph_projects')
      .select(columns)
      .order('updated_at', { ascending: false });

    if (options?.limit != null) {
      query = query.limit(options.limit);
    }

    const { data, error: projectsErr } = await query;

    if (projectsErr) {
      logProjectsQueryError('jphph_projects', role, profileId, projectsErr);
      return {
        rows: [],
        error: `Projects query failed (${projectsErr.code ?? 'unknown'}): ${projectsErr.message}`,
        profileId,
        role,
      };
    }

    const rows = (data ?? []) as Record<string, unknown>[];
    if (rows.length === 0) {
      console.warn(
        '[fetchRoleBasedProjects] management user received 0 rows from jphph_projects — verify RLS allows management user_type to SELECT all projects',
        { profileId, role }
      );
    }

    return { rows, error: null, profileId, role };
  }

  if (role === 'subcontractor') {
    const { rows, error: loadErr } = await fetchProjectsForSubcontractorMemberships(
      profileId,
      columns
    );

    if (loadErr) {
      return { rows: [], error: loadErr, profileId, role };
    }

    const limited = options?.limit != null ? rows.slice(0, options.limit) : rows;
    return { rows: limited, error: null, profileId, role };
  }

  if (role === 'client') {
    const { data: directProjects, error: directErr } = await supabase
      .from('jphph_projects')
      .select(columns)
      .eq('client_user_id', profileId)
      .order('updated_at', { ascending: false });

    if (directErr) {
      logProjectsQueryError('jphph_projects', role, profileId, directErr);
      return {
        rows: [],
        error: `Projects query failed (${directErr.code ?? 'unknown'}): ${directErr.message}`,
        profileId,
        role,
      };
    }

    const { data: memberRows, error: memberErr } = await supabase
      .from('jphph_project_members')
      .select('project_id')
      .eq('user_id', profileId)
      .eq('member_role', 'client');

    if (memberErr) {
      logProjectsQueryError('jphph_project_members', role, profileId, memberErr);
      return {
        rows: [],
        error: `Project members query failed (${memberErr.code ?? 'unknown'}): ${memberErr.message}`,
        profileId,
        role,
      };
    }

    const directIds = new Set((directProjects ?? []).map((p) => p.id as string));
    const memberProjectIds = Array.from(
      new Set(
        (memberRows ?? [])
          .map((r) => r.project_id as string)
          .filter((id) => id && !directIds.has(id))
      )
    );

    let memberProjects: Record<string, unknown>[] = [];
    if (memberProjectIds.length > 0) {
      const { data: memberProjectRows, error: memberProjectsErr } = await supabase
        .from('jphph_projects')
        .select(columns)
        .in('id', memberProjectIds)
        .order('updated_at', { ascending: false });

      if (memberProjectsErr) {
        logProjectsQueryError('jphph_projects', role, profileId, memberProjectsErr);
        return {
          rows: [],
          error: `Projects query failed (${memberProjectsErr.code ?? 'unknown'}): ${memberProjectsErr.message}`,
          profileId,
          role,
        };
      }

      memberProjects = (memberProjectRows ?? []) as Record<string, unknown>[];
    }

    const merged = [...((directProjects ?? []) as Record<string, unknown>[]), ...memberProjects];
    merged.sort(
      (a, b) =>
        new Date(String(b.updated_at ?? 0)).getTime() - new Date(String(a.updated_at ?? 0)).getTime()
    );

    const limited =
      options?.limit != null ? merged.slice(0, options.limit) : merged;

    return { rows: limited, error: null, profileId, role };
  }

  console.error('[fetchRoleBasedProjects] unsupported user_type', { profileId, role });
  return {
    rows: [],
    error: `Unsupported user_type: ${role || '(empty)'}`,
    profileId,
    role,
  };
  } catch (err) {
    console.error('[fetchRoleBasedProjects] unexpected error', err);
    return {
      rows: [],
      error: err instanceof Error ? err.message : 'Unexpected project load error',
      profileId: null,
      role: null,
    };
  }
}
